'use client';

import { FC, useState, useEffect } from 'react';
import { useParams, useRouter } from 'next/navigation';

import Step from '@mui/material/Step';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import StepLabel from '@mui/material/StepLabel';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Alert,
  Modal,
  Button,
  Stepper,
  Container,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';

import Image from '../../../../components/image';
import { useLocales } from '../../../../locales';
import Iconify from '../../../../components/iconify';
import { useBoolean } from '../../../../hooks/use-boolean';
import { useResponsive } from '../../../../hooks/use-responsive';
import { createVirtualCard } from '../../../../api/virtualCardType';
import { ActionTypes, useAppContext } from '../../../../contexts/AppContext';
import { BlockType, ComponentTypeProps } from '../../../../types/page-generator';
import {
  CardType,
  CardStatus,
  LimitFrequency,
  ICreateVirtualCardData,
} from '../../../../types/virtual-card';
import { getCustomer } from '../../../../api/citypeople';
import { useAuthContext } from '../../../../auth/hooks';
import { useSnackbar } from '../../../../components/snackbar';
import { IVirtualCard } from '../../../../types/club-cards';

interface ICustomerVirtualCard extends IVirtualCard {
  active: boolean;
  expiration: string;
}

const steps = ['CARDHOLDER', 'CARD DETAILS', 'SHIPPING', 'BILLING', 'REVIEW'];
const cardholders = [
  'Leon Adreas',
  'Vladimir Savrov',
  'Kamil Shufan',
  'kris kim',
  'Nati Amar',
  'rain test rain test',
  'ping lan',
  'asaf edry',
  'shonzino shonzino',
  'Abed Gadalla Mohamed',
];
const review = [
  { id: 1, name: 'Cardholder', title: '', icon: 'mdi:user' },
  {
    id: 2,
    name: 'Card Type',
    title: 'Physical',
    icon: 'famicons:card-outline',
  },
  {
    id: 3,
    name: 'Status',
    title: 'Not Activated',
    icon: 'line-md:switch-off-filled-to-switch-filled-transition',
  },
  {
    id: 4,
    name: 'Card Limit',
    title: '$1,000.00 (Per 30 Day)',
    icon: 'mdi:dollar',
  },
  { id: 5, name: 'Product ID', title: '404', icon: 'mdi:identifier' },
  {
    id: 6,
    name: 'Shipping Method',
    title: 'Standard',
    icon: 'material-symbols:local-shipping-rounded',
  },
  {
    id: 7,
    name: 'Shipping Address',
    title: '...',
    icon: 'fa-solid:map-marker-alt',
  },
  {
    id: 8,
    name: 'Billing Address',
    title: '...',
    icon: 'mdi:credit-card-marker',
  },
];

const CryptoCardDetails: FC<ComponentTypeProps> = ({ block }) => {
  const isMobile = useResponsive('down', 'sm');
  const { currentLang } = useLocales();
  const localParams = useParams();
  const router = useRouter();
  const { state, dispatch } = useAppContext();
  const { customer } = state;
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (user?.id) {
      getCustomer(user.id, dispatch);
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        cardholder: customer.foreignId || '',
        displayName: customer.firstName ? `${customer.firstName} ${customer.lastName || ''}` : '',
        phoneNumber: customer.phones?.[0]?.number || '',
        shippingAddress: {
          line1: customer.address?.line1 || '500 Market Street',
          line2: customer.address?.line2 || 'Suite 12A',
          city: customer.address?.city || 'San Francisco',
          region: customer.address?.region || 'California',
          postalCode: customer.address?.postalCode || '94105',
          countryCode: 'US',
          country: customer.address?.country || 'United States',
        },
        billingAddress: {
          line1: customer.address?.line1 || '500 Market Street',
          line2: customer.address?.line2 || 'Suite 12A',
          city: customer.address?.city || 'San Francisco',
          region: customer.address?.region || 'California',
          postalCode: customer.address?.postalCode || '94105',
          countryCode: 'US',
          country: customer.address?.country || 'United States',
        },
      }));
    }
  }, [customer]);

  const myCards =
    customer?.virtualCards?.map((card: ICustomerVirtualCard) => ({
      id: card.id,
      image: '/assets/images/card-1.png',
      name: card.cardType?.name || 'Virtual Card',
      info: `Balance: $${card.balance}`,
      status: card.active ? 'Active' : 'Inactive',
      cardNumber: card.cardNumber,
      expiration: new Date(card.expiration).toLocaleDateString(),
    })) || [];

  const firstStop = block?.settings?.firstGradientColorStop
    ? `${block?.settings?.firstGradientColorStop}%`
    : '40%';
  const secondStop = block?.settings?.secondGradientColorStop
    ? `${block?.settings?.secondGradientColorStop}%`
    : '100%';

  const colorGradient = block?.settings?.addGradientStyle
    ? `linear-gradient(90deg, ${block?.settings?.firstGradientColor} ${firstStop}, ${block?.settings?.secondGradientColor} ${secondStop} )`
    : '';

  const issueCard = useBoolean();
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [useCompanyAddress, setUseCompanyAddress] = useState(true);
  const [useShippingAddress, setUseShippingAddress] = useState(true);

  const [formData, setFormData] = useState({
    cardholder: customer?.foreignId || '',
    displayName: customer?.firstName ? `${customer.firstName} ${customer.lastName || ''}` : '',
    phoneNumber: customer?.phones?.[0]?.number || '',
    cardType: CardType.virtual,
    cardLimit: '1000',
    limitFrequency: LimitFrequency.per24HourPeriod,
    shippingMethod: 'Standard (US)',
    useCompanyAddress: true,
    useShippingAddress: true,
    shippingAddress: {
      line1: customer?.address?.line1 || '500 Market Street',
      line2: customer?.address?.line2 || 'Suite 12A',
      city: customer?.address?.city || 'San Francisco',
      region: customer?.address?.region || 'California',
      postalCode: customer?.address?.postalCode || '94105',
      countryCode: 'US',
      country: customer?.address?.country || 'United States',
    },
    billingAddress: {
      line1: customer?.address?.line1 || '500 Market Street',
      line2: customer?.address?.line2 || 'Suite 12A',
      city: customer?.address?.city || 'San Francisco',
      region: customer?.address?.region || 'California',
      postalCode: customer?.address?.postalCode || '94105',
      countryCode: 'US',
      country: customer?.address?.country || 'United States',
    },
  });

  const isStepOptional = (step: number) => step === 1;

  const isStepSkipped = (step: number) => skipped.has(step);

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (type: 'shipping' | 'billing', field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}Address`]: {
        ...prev[`${type}Address`],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      const cardData: ICreateVirtualCardData = {
        customerId: customer?.id || '',
        virtualCardTypeId: '2fd4be2a-4ac4-4f50-9280-41395b9f2366',
        crmProjectId: 5,
        type: formData.cardType,
        status: CardStatus.active,
        limit: {
          amount: Number(formData.cardLimit),
          frequency: formData.limitFrequency,
        },
        configuration: {
          displayName: formData.displayName,
          productId: 1,
          productRef: 'default-card',
        },
        shipping: {
          line1: formData.shippingAddress.line1 || '500 Market Street',
          line2: formData.shippingAddress.line2 || 'Suite 12A',
          city: formData.shippingAddress.city || 'San Francisco',
          region: formData.shippingAddress.region || 'California',
          postalCode: formData.shippingAddress.postalCode || '94105',
          countryCode: 'US',
          country: formData.shippingAddress.country || 'United States',
          phoneNumber: formData.phoneNumber || '+1-415-555-1234',
        },
        billing: {
          line1: formData.billingAddress.line1 || '500 Market Street',
          line2: formData.billingAddress.line2 || 'Suite 12A',
          city: formData.billingAddress.city || 'San Francisco',
          region: formData.billingAddress.region || 'California',
          postalCode: formData.billingAddress.postalCode || '94105',
          countryCode: 'US',
          country: formData.billingAddress.country || 'United States',
        },
      };

      console.log('Sending card data:', cardData);

      const response = await createVirtualCard(cardData);
      if (response?.payload?.customer) {
        dispatch({
          type: ActionTypes.GET_CUSTOMER_BY_SSO_ID,
          payload: response.payload.customer,
        });
        issueCard.onFalse();
        enqueueSnackbar('Card created successfully!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error creating card:', error);
      enqueueSnackbar('Error creating card. Please try again.', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ p: 0 }}>
      {block?.settings?.enableCardService && <CardServicePanelOverview block={block} />}
      {block?.settings?.enableMyCars && (
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" sx={{ my: 2, color: 'black' }}>
            My Cards
          </Typography>
          <Stack
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
          >
            <Iconify icon="ph:arrow-up-bold" sx={{ color: 'red', transform: 'rotate(45deg)' }} />
            <Typography variant="h6" sx={{ my: 2, color: 'red' }}>
              All ({myCards.length})
            </Typography>
          </Stack>
        </Stack>
      )}
      {block?.settings?.enableMyCars && (
        <Stack width={1} gap={2}>
          {myCards.map(
            (
              card: {
                id: string;
                image: string;
                name: string;
                info: string;
                status: string;
                cardNumber: string;
                expiration: string;
              },
              id: number
            ) => (
              <Stack
                key={id}
                direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
                width={1}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  background: block?.settings?.addGradientStyle
                    ? colorGradient
                    : block?.settings?.cardColor || '#2b1d36',
                  borderRadius: '20px',
                  p: 2,
                  py: 4,
                  position: 'relative',
                }}
                onClick={() => {
                  router.push(`/${localParams.lng}/card`);
                }}
              >
                <Box
                  sx={
                    currentLang.value === 'he'
                      ? {
                          position: 'absolute',
                          top: 0,
                          left: '50px',
                          background: card.status === 'Active' ? 'green' : 'red',
                          borderBottomLeftRadius: '10px',
                          borderBottomRightRadius: '10px',
                          p: 0.5,
                        }
                      : {
                          position: 'absolute',
                          top: 0,
                          right: '50px',
                          background: card.status === 'Active' ? 'green' : 'red',
                          borderBottomLeftRadius: '10px',
                          borderBottomRightRadius: '10px',
                          p: 0.5,
                        }
                  }
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '8px',
                    }}
                  >
                    {card.status}
                  </Typography>
                </Box>
                <Image src={card.image} />
                <Stack>
                  <Typography sx={{ color: 'white' }}>{card.name}</Typography>
                  <Typography sx={{ color: '#F5F5F5' }}>{card.info}</Typography>
                  <Typography sx={{ color: '#F5F5F5', fontSize: '12px' }}>
                    {card.cardNumber}
                  </Typography>
                  <Typography sx={{ color: '#F5F5F5', fontSize: '12px' }}>
                    Expires: {card.expiration}
                  </Typography>
                </Stack>
                <Iconify icon="oui:arrow-right" sx={{ color: 'white' }} />
              </Stack>
            )
          )}
        </Stack>
      )}
      {block?.settings?.enableNewCars && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="h6"
            sx={{
              my: 2,
              color: 'black',
              textAlign: currentLang.value === 'he' ? 'end' : 'start',
            }}
          >
            New card
          </Typography>
          <IconButton
            sx={{ bgcolor: 'green', '&:hover': { bgcolor: 'green' } }}
            onClick={issueCard.onTrue}
          >
            <Iconify icon="line-md:plus" sx={{ color: 'white' }} />
          </IconButton>
        </Stack>
      )}
      {block?.settings?.enableNewCars && !myCards.length && (
        <Stack alignItems="center" justifyContent="center">
          <Image src="/assets/images/no-data.png" />
        </Stack>
      )}
      <Modal open={issueCard.value} onClose={issueCard.onFalse}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : '600px',
            maxHeight: isMobile ? '95vh' : '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stepper
            activeStep={activeStep}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: isMobile ? 2 : 4, overflowX: 'auto' }}
          >
            {steps.map((label, index) => {
              const stepProps: any = {};
              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              px: isMobile ? 1 : 2,
              pt: isMobile ? 1 : 2,
            }}
          >
            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  required
                  label="Cardholder ID"
                  helperText="Cardholder ID is required"
                  value={formData.cardholder}
                  onChange={(e) => handleInputChange('cardholder', e.target.value)}
                  disabled={!!customer}
                />
                <TextField
                  label="Display Name on Card"
                  helperText="Name that will be displayed on the card"
                  inputProps={{ maxLength: 30 }}
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  disabled={!!customer}
                />
                <TextField
                  required
                  label="Phone Number"
                  helperText="Required for physical card delivery (Format: +1234567890)"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  disabled={!!customer}
                />
              </Box>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Card Type"
                    value={formData.cardType}
                    onChange={(e) => handleInputChange('cardType', e.target.value)}
                  >
                    <MenuItem value={CardType.physical}>Physical</MenuItem>
                    <MenuItem value={CardType.virtual}>Virtual</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    required
                    label="Card Limit"
                    value={formData.cardLimit}
                    onChange={(e) => handleInputChange('cardLimit', e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Limit Frequency"
                    value={formData.limitFrequency}
                    onChange={(e) => handleInputChange('limitFrequency', e.target.value)}
                  >
                    <MenuItem value={LimitFrequency.per24HourPeriod}>Per 24 Hours</MenuItem>
                    <MenuItem value={LimitFrequency.perMonth}>Per Month</MenuItem>
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Shipping Method"
                    value={formData.shippingMethod}
                    onChange={(e) => handleInputChange('shippingMethod', e.target.value)}
                  >
                    <MenuItem value="Standard (US)">Standard (US)</MenuItem>
                    <MenuItem value="Express (US)">Express (US)</MenuItem>
                  </TextField>
                </Box>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography fontWeight="bold">Physical Card Requirements</Typography>
                  For physical card delivery, the cardholder must have a valid phone number
                  associated with their account.
                </Alert>
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useCompanyAddress}
                      onChange={(e) => handleInputChange('useCompanyAddress', e.target.checked)}
                    />
                  }
                  label="Use company address"
                />
                {!formData.useCompanyAddress && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Address Line 1"
                        value={formData.shippingAddress.line1}
                        onChange={(e) => handleAddressChange('shipping', 'line1', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 2"
                        value={formData.shippingAddress.line2}
                        onChange={(e) => handleAddressChange('shipping', 'line2', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="City"
                        value={formData.shippingAddress.city}
                        onChange={(e) => handleAddressChange('shipping', 'city', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="State/Region"
                        value={formData.shippingAddress.region}
                        onChange={(e) => handleAddressChange('shipping', 'region', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="Postal Code"
                        value={formData.shippingAddress.postalCode}
                        onChange={(e) =>
                          handleAddressChange('shipping', 'postalCode', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        select
                        label="Country"
                        required
                        value={formData.shippingAddress.country}
                        onChange={(e) => handleAddressChange('shipping', 'country', e.target.value)}
                      >
                        <MenuItem value="Israel">Israel</MenuItem>
                        <MenuItem value="USA">USA</MenuItem>
                        <MenuItem value="Germany">Germany</MenuItem>
                        <MenuItem value="France">France</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useShippingAddress}
                      onChange={(e) => handleInputChange('useShippingAddress', e.target.checked)}
                    />
                  }
                  label="Same as shipping address"
                />
                {!formData.useShippingAddress && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Address Line 1"
                        value={formData.billingAddress.line1}
                        onChange={(e) => handleAddressChange('billing', 'line1', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 2"
                        value={formData.billingAddress.line2}
                        onChange={(e) => handleAddressChange('billing', 'line2', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="City"
                        value={formData.billingAddress.city}
                        onChange={(e) => handleAddressChange('billing', 'city', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="State/Region"
                        value={formData.billingAddress.region}
                        onChange={(e) => handleAddressChange('billing', 'region', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        required
                        label="Postal Code"
                        value={formData.billingAddress.postalCode}
                        onChange={(e) =>
                          handleAddressChange('billing', 'postalCode', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        select
                        label="Country"
                        required
                        value={formData.billingAddress.country}
                        onChange={(e) => handleAddressChange('billing', 'country', e.target.value)}
                      >
                        <MenuItem value="Israel">Israel</MenuItem>
                        <MenuItem value="USA">USA</MenuItem>
                        <MenuItem value="Germany">Germany</MenuItem>
                        <MenuItem value="France">France</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {activeStep === 4 && (
              <Stack spacing={2}>
                <Typography fontWeight={700}>Review Card Details</Typography>
                {review.map((item, id) => (
                  <Stack key={id} direction="row" spacing={2} alignItems="center">
                    <Iconify icon={item.icon} width={24} height={24} />
                    <Stack>
                      <Typography variant="subtitle1">{item.name}</Typography>
                      {item.title && (
                        <Typography variant="body2" color="text.secondary">
                          {item.title}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}>
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

type Props = {
  block: BlockType;
};

function CardServicePanelOverview({ block }: Props) {
  const combinedBlock: any = block?.combinedBlock || [];
  const localParams = useParams();
  const router = useRouter();
  const { currentLang } = useLocales();
  const { dispatch, state } = useAppContext();

  const setActiveDialog = (payload: null | string) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_DIALOG, payload });
  };

  const firstStopNavigate = block?.settings?.firstGradientColorStopNavigate
    ? `${block?.settings?.firstGradientColorStopNavigate}%`
    : '40%';
  const secondStopNavigate = block?.settings?.secondGradientColorStopNavigate
    ? `${block?.settings?.secondGradientColorStopNavigate}%`
    : '100%';

  const colorGradientNavigate = block?.settings?.addGradientStyleNavigate
    ? `linear-gradient(90deg, ${block?.settings?.firstGradientColorNavigate} ${firstStopNavigate}, ${block?.settings?.secondGradientColorNavigate} ${secondStopNavigate} )`
    : '';

  const view = (item: any) =>
    block?.settings?.variant === '1' ? (
      <ButtonBase
        sx={{
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'inline-block',
        }}
      >
        <Paper
          elevation={6} // Чем больше — тем выше тень
          sx={{
            backgroundColor: '#fff', // Или другой цвет фона, не 'transparent'
            borderRadius: '50%',
            // border: '1px solid black',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 80,
            width: 80,
            boxShadow: (theme) => theme.shadows[6], // можно явно указать тень
          }}
        >
          <Iconify
            icon={item.buttonIcon}
            sx={{
              width: 30,
              height: 30,
              color: item.buttonColor || '#FFC107',
            }}
          />
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              color: item.buttonColor || '#FFC107',
              fontSize: item?.buttonFontSize || '12px',
              textAlign: 'center',
            }}
          >
            {item.buttonTitle}
          </Typography>
        </Paper>
      </ButtonBase>
    ) : (
      <Paper
        elevation={6}
        sx={{
          background: block?.settings?.addGradientStyleNavigate ? colorGradientNavigate : '',
          // backgroundImage: `url('/assets/images/button-font.png')`,
          borderRadius: 3,
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 120,
          width: 180,
        }}
      >
        <Iconify
          icon={item.buttonIcon}
          sx={{
            width: '30px',
            height: '30px',
            color: item.buttonColor || '#FFC107',
          }}
        />
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            color: item.buttonColor || '#FFC107',
            fontSize: item?.buttonFontSize || '12px',
            textAlign: 'center',
          }}
        >
          {item.buttonTitle}
        </Typography>
      </Paper>
    );

  return (
    <Box sx={{ textAlign: 'left', color: 'white' }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color: 'black',
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        Card Service
      </Typography>

      <Stack alignItems="center">
        <Box
          sx={{
            width: block?.settings?.serviceButtonVariant === '2' ? '70%' : '100%',
          }}
        >
          {/* Верхний ряд: 2 карточки */}
          <Grid container spacing={2} justifyContent="center">
            {combinedBlock.map((item: any, index: number) => (
              <Grid
                item
                xs={block?.settings?.serviceButtonVariant === '2' ? 6 : 4}
                key={index}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  if (item?.buttonHref && item?.buttonHref !== '#' && !item?.buttonDrawer) {
                    router.push(`/${localParams.lng}/${item?.buttonHref}`);
                  }
                  if (
                    !item?.buttonHref &&
                    item?.buttonHref === '#' &&
                    item.buttonDrawer &&
                    item.buttonModal
                  ) {
                    setActiveDialog(item.buttonModal);
                  }
                }}
              >
                {view(item)}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
}

export default CryptoCardDetails;
